class CartShippingCalculator extends HTMLElement {
  connectedCallback() {
    this._select = this.querySelector('.cart-shipping-calc__select');
    this._result = this.querySelector('.cart-shipping-calc__result');
    this._price = this.querySelector('.cart-shipping-calc__price');
    this._deliveryRow = this.querySelector('.cart-shipping-calc__delivery-row');
    this._delivery = this.querySelector('.cart-shipping-calc__delivery');
    this._loading = this.querySelector('.cart-shipping-calc__loading');
    this._error = this.querySelector('.cart-shipping-calc__error');

    // Restore saved governorate selection
    const saved = localStorage.getItem('grillit_shipping_province');
    if (saved) this._select.value = saved;

    const onSelectChange = () => {
      const province = this._select.value;
      if (province) {
        localStorage.setItem('grillit_shipping_province', province);
        this._fetchRates(province);
      } else {
        this._reset();
        localStorage.removeItem('grillit_shipping_province');
      }
    };

    // Listen to both change and input — input fires when value is set programmatically
    this._select.addEventListener('change', onSelectChange);
    this._select.addEventListener('input', onSelectChange);

    // Fetch immediately if a province is already selected on load
    if (this._select.value) this._fetchRates(this._select.value);

    // Re-fetch when cart total changes (free shipping threshold may activate).
    // Guard against re-triggering when _updateEstimatedTotal modifies the element.
    const subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (subtotalEl) {
      new MutationObserver(() => {
        if (this._isUpdatingTotal) return;
        const province = this._select?.value;
        if (province) this._fetchRates(province);
      }).observe(subtotalEl, { childList: true, subtree: true, characterData: true });
    }
  }

  async _fetchRates(province) {
    this._showLoading();
    try {
      const params = new URLSearchParams({
        'shipping_address[country]': 'EG',
        'shipping_address[province]': province,
        'shipping_address[zip]': '11511',
        'shipping_address[city]': 'city',
      });
      const res = await fetch(`/cart/shipping_rates.json?${params}`);
      if (!res.ok) throw new Error('bad response');
      const { shipping_rates: rates } = await res.json();

      if (!rates || rates.length === 0) {
        this._showError('Shipping not available for this location.');
        return;
      }

      // Prefer free shipping; otherwise pick lowest price
      const best =
        rates.find((r) => parseFloat(r.price) === 0) ||
        rates.reduce((a, b) => (parseFloat(a.price) <= parseFloat(b.price) ? a : b));

      this._showResult(best);
    } catch {
      this._showError('Could not calculate shipping. Please continue to checkout.');
    }
  }

  _updateEstimatedTotal(shippingPrice) {
    const totalEl = this.closest('.cart-totals')?.querySelector('[data-cart-subtotal]');
    if (!totalEl) return;

    // Save original text once so we can restore it on reset
    if (!this._originalTotalText) {
      this._originalTotalText = totalEl.textContent.trim();
    }

    this._isUpdatingTotal = true;

    if (shippingPrice === null) {
      totalEl.textContent = this._originalTotalText;
      // Defer reset — MutationObserver fires as microtask, must clear after it
      Promise.resolve().then(() => { this._isUpdatingTotal = false; });
      return;
    }

    const cartTotalCents = parseInt(this.dataset.cartTotal || '0', 10);
    const shippingCents = Math.round(shippingPrice * 100);
    const newTotalAmount = (cartTotalCents + shippingCents) / 100;

    // Replace the numeric part in the original formatted string, preserving currency symbols
    const numMatch = this._originalTotalText.match(/[\d,]+\.?\d*/);
    if (numMatch) {
      const numStr = newTotalAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      totalEl.textContent = this._originalTotalText.replace(numMatch[0], numStr);
    }

    // Defer reset — MutationObserver fires as microtask, must clear after it
    Promise.resolve().then(() => { this._isUpdatingTotal = false; });
  }

  _showResult(rate) {
    this._loading.hidden = true;
    this._error.hidden = true;

    const price = parseFloat(rate.price);
    this._price.textContent = price === 0 ? 'Free' : `EGP ${Math.round(price)}`;

    // Update the estimated total to include shipping
    this._updateEstimatedTotal(price);

    // Parse delivery days from rate name.
    // Convention: include "(X days)" or "(X-Y days)" in Shopify Admin rate name.
    // Example: "Cairo + Nearby Governorates (1-2 days)"
    const match = rate.name.match(/\(?(\d+)\s*(?:[-–]\s*(\d+))?\s*days?\)?/i);
    if (match) {
      const minDays = Math.max(1, parseInt(match[1], 10));
      const maxDays = match[2] ? Math.max(minDays, parseInt(match[2], 10)) : minDays;
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const addDays = (n) => {
        const d = new Date();
        d.setDate(d.getDate() + n);
        return dayNames[d.getDay()];
      };
      this._delivery.textContent =
        minDays === maxDays
          ? `Arrives ${addDays(minDays)}`
          : `Arrives ${addDays(minDays)} – ${addDays(maxDays)}`;
      this._deliveryRow.hidden = false;
    } else {
      this._deliveryRow.hidden = true;
    }

    this._result.hidden = false;
  }

  _showLoading() {
    this._loading.hidden = false;
    this._result.hidden = true;
    this._error.hidden = true;
  }

  _showError(msg) {
    this._loading.hidden = true;
    this._result.hidden = true;
    this._error.hidden = false;
    this._error.textContent = msg;
    this._updateEstimatedTotal(null);
  }

  _reset() {
    this._result.hidden = true;
    this._error.hidden = true;
    this._loading.hidden = true;
    this._updateEstimatedTotal(null);
  }
}

customElements.define('cart-shipping-calculator', CartShippingCalculator);
